import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

const siteUrl = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_SITE_URL || 'https://sugargoosheet.site' : 'https://sugargoosheet.site';
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg';

function parseDateToISO(dateStr: string): string {
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }
  return '2026-08-06';
}

function buildArticleSchema(article: ReturnType<typeof getArticleBySlug>) {
  if (!article) return null;
  const articleUrl = `${siteUrl}/article/${article.slug}`;
  const isoDate = parseDateToISO(article.date);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.imageUrl,
    datePublished: isoDate,
    dateModified: isoDate,
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };
}

function buildFAQSchema(article: ReturnType<typeof getArticleBySlug>) {
  if (!article) return null;
  const faqSections = article.sections.filter((s) => s.type === 'faq' && s.questions);
  if (faqSections.length === 0) return null;
  const allQuestions = faqSections.flatMap((s) => s.questions || []);
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allQuestions.map((q) => ({
      '@type': 'Question',
      name: q.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.a,
      },
    })),
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const resolvedSlug = slug || 'sugargoo-spreadsheet-guide';
  const article = getArticleBySlug(resolvedSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [resolvedSlug]);

  if (!article) {
    return <NotFound />;
  }

  const articleSchema = buildArticleSchema(article);
  const faqSchema = buildFAQSchema(article);

  return (
    <>
      {articleSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      )}
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <title>{article.title}</title>
      <meta name="description" content={article.metaDescription} />
      <meta name="keywords" content={article.keywords} />
      <link rel="canonical" href={`https://sugargoosheet.site/article/${article.slug}`} />

      <Navbar />
      <main>
        <section className="pt-24 pb-12 md:pt-32 md:pb-16 bg-background-50">
          <div className="max-w-screen-lg mx-auto px-4 md:px-6">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors">
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground-800 font-medium">{article.category}</span>
            </nav>

            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider mb-4">
              {article.badge} &bull; 2026 Edition
            </span>

            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              {article.title.split(':')[0]}
              {article.title.includes(':') && (
                <>
                  :{' '}
                  <span className="text-primary-600 italic">{article.title.split(':').slice(1).join(':')}</span>
                </>
              )}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Updated: {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readingTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                50,000+ readers
              </span>
            </div>
          </div>
        </section>

        <section className="bg-background-50 pb-8">
          <div className="max-w-screen-lg mx-auto px-4 md:px-6">
            <div className="rounded-2xl overflow-hidden aspect-[21/9]">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-screen-lg mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-12">
              <aside className="lg:w-64 flex-shrink-0">
                <div className="sticky top-24 bg-background-100 rounded-xl p-5 border border-background-200/70">
                  <h4 className="font-semibold text-foreground-900 text-sm mb-4">Table of Contents</h4>
                  <nav className="space-y-2">
                    {article.sections
                      .filter((s) => s.heading)
                      .map((section, idx) => (
                        <a
                          key={idx}
                          href={`#section-${idx}`}
                          className="block text-xs text-foreground-600 hover:text-primary-600 transition-colors leading-relaxed"
                        >
                          {section.heading}
                        </a>
                      ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="space-y-12">
                  {article.sections.map((section, idx) => (
                    <section key={idx} id={`section-${idx}`}>
                      {section.heading && (
                        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                          {section.heading}
                        </h2>
                      )}

                      {section.type === 'content' && section.content && (
                        <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                          {section.content.map((p, pi) => (
                            <p key={pi}>{p}</p>
                          ))}
                        </div>
                      )}

                      {section.type === 'table' && section.rows && (
                        <div className="overflow-x-auto mt-4">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-background-200">
                                <th className="text-left py-3 pr-4 font-semibold text-foreground-800">Feature</th>
                                <th className="text-left py-3 pr-4 font-semibold text-primary-600">Sugargoo</th>
                                <th className="text-left py-3 font-semibold text-foreground-400">Alternative</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.rows.map((row) => (
                                <tr key={row.label} className="border-b border-background-200/50">
                                  <td className="py-3 pr-4 font-medium text-foreground-700">{row.label}</td>
                                  <td className="py-3 pr-4 text-primary-600 font-medium">{row.value1}</td>
                                  <td className="py-3 text-foreground-500">{row.value2}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {section.type === 'tips' && section.tips && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                          {section.tips.map((tip) => (
                            <div key={tip.title} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70">
                              <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                                <i className="ri-lightbulb-line text-sm text-accent-700"></i>
                              </span>
                              <div>
                                <strong className="text-foreground-900 block text-sm">{tip.title}</strong>
                                <span className="text-xs text-foreground-500">{tip.desc}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {section.type === 'faq' && section.questions && (
                        <div className="space-y-3">
                          {section.questions.map((faq, fi) => (
                            <details key={fi} className="group bg-background-100 rounded-xl border border-background-200/70">
                              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                                <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                                <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-background-200 group-open:bg-accent-100 transition-colors">
                                  <i className="ri-add-line text-xs text-foreground-500 group-open:hidden"></i>
                                  <i className="ri-subtract-line text-xs text-accent-700 hidden group-open:block"></i>
                                </span>
                              </summary>
                              <div className="px-5 pb-4">
                                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                              </div>
                            </details>
                          ))}
                        </div>
                      )}
                    </section>
                  ))}

                  <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6 md:p-8 text-center">
                    <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                      Ready to Start Shopping?
                    </h3>
                    <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                      Explore the full Sugargoo Spreadsheet and discover thousands of curated products. Back to the{' '}
                      <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium underline">
                        Sugargoo Spreadsheet homepage
                      </Link>{' '}
                      for more resources.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Sugargoo Spreadsheet
                      </a>
                      <Link
                        to="/"
                        className="w-full sm:w-auto px-8 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Back to Homepage
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}